import { Alert, Divider, Stack } from '@mui/material';
import { FormDialog, FormTextField } from '../../../design-system';
import { characterLength } from '../model/companyLegal';
import type { CompanyCreateInput, CompanyLegalProfile } from '../model/types';
import { CompanyLegalFields } from './CompanyLegalFields';

type AffiliateCreateDialogProps = {
  open: boolean;
  creating: boolean;
  createValid: boolean;
  createDraft: CompanyCreateInput;
  createProfile: CompanyLegalProfile;
  createError?: string;
  onClose: () => void;
  onSubmit: () => void;
  onChange: (field: keyof CompanyCreateInput, value: string) => void;
};

export function AffiliateCreateDialog({
  open,
  creating,
  createValid,
  createDraft,
  createProfile,
  createError,
  onClose,
  onSubmit,
  onChange,
}: AffiliateCreateDialogProps) {
  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      title="계열사 추가"
      cancelLabel="취소"
      submitLabel="계열사 추가"
      busyLabel="추가하는 중…"
      busy={creating}
      submitDisabled={!createValid}
      maxWidth="md"
    >
      <Stack
        direction={{
          xs: 'column',
          sm: 'row',
        }}
        spacing={2}
      >
        <FormTextField
          required
          fullWidth
          label="회사 코드"
          value={createDraft.companyCode}
          error={
            createDraft.companyCode !== '' &&
            !/^[A-Z][A-Z0-9._-]{0,63}$/.test(createDraft.companyCode.trim().toUpperCase())
          }
          helperText="영문자로 시작하고 영문 대문자, 숫자, ., _, -만 사용합니다."
          onChange={(event) => onChange('companyCode', event.target.value.toUpperCase())}
          slotProps={{
            htmlInput: {
              maxLength: 64,
              autoFocus: true,
            },
          }}
        />
        <FormTextField
          required
          fullWidth
          label="표시 이름"
          value={createDraft.descriptor}
          error={
            createDraft.descriptor !== '' &&
            (characterLength(createDraft.descriptor) < 1 || characterLength(createDraft.descriptor) > 120)
          }
          onChange={(event) => onChange('descriptor', event.target.value)}
          slotProps={{
            htmlInput: {
              maxLength: 120,
            },
          }}
        />
        <FormTextField
          required
          label="국가 코드"
          value={createDraft.countryCode}
          error={!/^[A-Z]{2}$/.test(createDraft.countryCode.trim().toUpperCase())}
          helperText="ISO 2자리 코드"
          onChange={(event) => onChange('countryCode', event.target.value.toUpperCase())}
          slotProps={{
            htmlInput: {
              maxLength: 2,
              size: 4,
            },
          }}
          sx={{
            width: {
              xs: '100%',
              sm: 144,
            },
            flexShrink: 0,
          }}
        />
      </Stack>
      <Divider />
      <CompanyLegalFields profile={createProfile} disabled={creating} readOnly={false} onChange={onChange} />
      {createError ? <Alert severity="error">{createError}</Alert> : null}
    </FormDialog>
  );
}
